import Mango from './mango'
import { getUser } from './user'
import { logoutSession as performLogout } from './logoutSession.js'
import { getSessionApi } from './sessionApi.js'
import { readStoredMemberId, readStoredToken, restoreShopper } from './browserSession.js'

export { logoutSession as performLogout } from './logoutSession.js'

function setAuthCookie(value) {
	if (!value) {
		document.cookie = 'Authorization=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/'
		return
	}
	const expires = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toUTCString()
	document.cookie = `Authorization=${value};expires=${expires};path=/`
}

/**
 * Every storefront logout goes through here: session storage, the Authorization
 * cookie, axios, the Mango client header, and a fresh empty guest cart.
 */
export function logoutSession(options = {}) {
	return performLogout({
		clearMangoAuthorization: () => Mango.clearAuthorization?.(),
		...options,
	})
}

export function parseAuthToken(token) {
	if (!token || typeof token !== 'string') return null
	const colon = token.indexOf(':')
	if (colon === -1) return null
	return { hash: token.slice(0, colon), memberId: token.slice(colon + 1) }
}

export function storedAuthorization() {
	let token = ''
	try {
		token = window.localStorage.getItem('token') || ''
	} catch {
		token = ''
	}
	if (token) return token
	const match = document.cookie.match(/(?:^|;\s*)Authorization=([^;]*)/)
	return match ? decodeURIComponent(match[1]) : ''
}

export function syncAuthHeader(axios) {
	const token = window.localStorage.getItem('token')
	if (token) {
		axios.defaults.headers.common['Authorization'] = token
	} else {
		delete axios.defaults.headers.common['Authorization']
	}
	return token
}

export function clearSession(store, axios) {
	store.user = {}
	window.user = null
	window.localStorage.removeItem('user')
	window.localStorage.removeItem('token')
	window.localStorage.removeItem('email')
	window.localStorage.removeItem('auth')
	setAuthCookie('')
	delete axios.defaults.headers.common['Authorization']
	delete axios.defaults.headers.common['authorization']
	Mango.clearAuthorization?.()
}

export function getSessionConsistency() {
	// Screened reads: an id stored as the literal "undefined" would otherwise read
	// as a session and reach Mango.member() below.
	const userId = readStoredMemberId()
	const token = readStoredToken()

	if (!userId && !token) return { valid: false, reason: 'guest' }
	if (!userId || !token) return { valid: false, reason: 'storage_mismatch' }

	const parsed = parseAuthToken(token)
	if (!parsed?.memberId || parsed.memberId !== userId) {
		return { valid: false, reason: 'token_user_mismatch' }
	}

	return { valid: true, userId, token }
}

const TRANSPORT_ERROR_CODES = new Set(['ECONNABORTED', 'ERR_NETWORK', 'ETIMEDOUT'])

/**
 * A stalled XHR (status 0 / "timeout exceeded"), a dropped connection, or any
 * error that never received an HTTP response. These are not auth decisions.
 */
function isTransportFailure(error) {
	if (!error || typeof error !== 'object') return true
	const status = error.response?.status
	if (status === 401 || status === 403) return false
	const message = typeof error.message === 'string' ? error.message : ''
	const requestStatus = error.request?.status
	if (!error.response) return true
	if (status === 0 || error.status === 0) return true
	if (requestStatus === 0 && (status === undefined || status === 0)) return true
	if (TRANSPORT_ERROR_CODES.has(error.code)) return true
	if (message === 'timeout exceeded' || message.includes('timeout exceeded')) return true
	return false
}

/**
 * Clear only when the server rejects the credential or the body has no member.
 * Transport failures and HTTP 5xx (and any other status) leave the stored session.
 * Never rethrows: resume listeners have no .catch.
 */
function applySessionRequestError(error, store, axios) {
	if (isTransportFailure(error)) return 'kept'
	const status = error?.response?.status
	if (status === 401 || status === 403) {
		clearSession(store, axios)
		return 'cleared'
	}
	return 'kept'
}

/**
 * Reconcile localStorage, axios Authorization, and store.user with the backend.
 * Returns { valid: true } when the shopper has a live session, otherwise { valid: false, reason }.
 * A transport failure or HTTP 5xx leaves the stored session and returns reason 'unavailable'.
 */
async function restoredCookieMember(store, axios) {
	if (!getSessionApi()) return null
	const restored = await restoreShopper({ axios })
	if (restored?.unavailable && !restored?.id) return { unavailable: true }
	if (restored?.id) {
		store.user = restored
		return restored
	}
	return null
}

export async function validateSession({ store, axios }) {
	syncAuthHeader(axios)

	const restored = await restoredCookieMember(store, axios)
	if (restored?.unavailable) {
		const consistency = getSessionConsistency()
		if (!consistency.valid) return { valid: false, reason: store.user?.id ? 'unavailable' : 'guest' }
	} else if (restored?.id) {
		return { valid: true }
	}

	const consistency = getSessionConsistency()
	if (!consistency.valid) {
		if (store.user?.id) clearSession(store, axios)
		return { valid: false, reason: consistency.reason }
	}

	try {
		const member = await Mango.member(consistency.userId, { depthLimit: 0, fields: ['id', 'email', 'firstName', 'lastName', 'bcId'] })
		if (!member?.id) {
			clearSession(store, axios)
			return { valid: false, reason: 'api_rejected' }
		}
		store.user = member
		return { valid: true }
	} catch (error) {
		if (applySessionRequestError(error, store, axios) === 'cleared') {
			return { valid: false, reason: 'api_rejected' }
		}
		return { valid: false, reason: 'unavailable' }
	}
}

/**
 * Lightweight check used on resume: sync storage/header drift and reload store.user when needed.
 */
export async function refreshSessionOnResume({ store, axios }) {
	syncAuthHeader(axios)

	const restored = await restoredCookieMember(store, axios)
	if (restored?.unavailable) return
	if (restored?.id) return

	const consistency = getSessionConsistency()
	if (!consistency.valid) {
		if (store.user?.id || window.localStorage.getItem('token')) {
			clearSession(store, axios)
		}
		return
	}

	if (store.user?.id === consistency.userId) {
		try {
			const member = await Mango.member(consistency.userId, { depthLimit: 0, fields: ['id'] })
			if (!member?.id) clearSession(store, axios)
		} catch (error) {
			applySessionRequestError(error, store, axios)
		}
		return
	}

	try {
		const member = await getUser()
		if (member?.id) {
			store.user = member
		} else {
			clearSession(store, axios)
		}
	} catch (error) {
		applySessionRequestError(error, store, axios)
	}
}

let resumeListenersInstalled = false

/**
 * iOS Safari (and other mobile browsers) restore tabs from memory/bfcache with stale Vue state.
 * visibilitychange + pageshow(persisted) + focus are the standard resume signals.
 */
export function installSessionResumeListeners({ store, axios }) {
	if (resumeListenersInstalled || typeof window === 'undefined') return
	resumeListenersInstalled = true

	let validating = false
	let focusTimer = null

	const onResume = () => {
		if (validating) return
		const savedUser = window.localStorage.getItem('user')
		if (!store.user?.id && !window.localStorage.getItem('token') && !savedUser) return

		validating = true
		refreshSessionOnResume({ store, axios }).finally(() => {
			validating = false
		})
	}

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'visible') onResume()
	})

	window.addEventListener('pageshow', (event) => {
		if (event.persisted) onResume()
	})

	window.addEventListener('focus', () => {
		clearTimeout(focusTimer)
		focusTimer = setTimeout(onResume, 300)
	})
}
